import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      setPreferences(JSON.parse(consent));
    }
  }, []);

  const savePreferences = (newPreferences) => {
    setPreferences(newPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    savePreferences(onlyNecessary);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 z-50 border-t border-gray-200">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex-1 mb-4 md:mb-0 md:mr-8">
                <h3 className="font-semibold text-gray-800 mb-1">Uso de Cookies</h3>
                <p className="text-sm text-gray-600">
                  Utilizamos cookies propias y de terceros para mejorar tu experiencia de navegación, analizar el uso del sitio y ofrecerte contenido personalizado. 
                  Al hacer clic en "Aceptar todas", aceptas el uso de todas las cookies según nuestra 
                  <a href="/aviso-de-privacidad" className="text-blue-600 hover:underline ml-1">
                    Política de Privacidad
                  </a>.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  El uso de estas cookies se rige por nuestro Aviso de Privacidad y cumple con la LFPDPPP.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full md:w-auto">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Configurar cookies
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Configuración de Cookies</h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. A continuación, puedes personalizar tus preferencias.
                  Para obtener más información, consulta nuestro 
                  <a href="/aviso-de-privacidad" className="text-blue-600 hover:underline">
                    Aviso de Privacidad
                  </a>.
                </p>
              </div>

              <div className="space-y-6 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="necessary"
                      name="necessary"
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="necessary" className="font-medium text-gray-700">
                      Cookies Necesarias
                    </label>
                    <p className="text-gray-500">
                      Son esenciales para que el sitio web funcione correctamente. Incluyen cookies de seguridad y funcionalidad básica.
                    </p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="analytics"
                      name="analytics"
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="analytics" className="font-medium text-gray-700">
                      Cookies de Análisis
                    </label>
                    <p className="text-gray-500">
                      Nos permiten analizar el uso del sitio web para mejorar su rendimiento y contenido.
                    </p>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="functional"
                      name="functional"
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => togglePreference('functional')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="functional" className="font-medium text-gray-700">
                      Cookies de Funcionalidad
                    </label>
                    <p className="text-gray-500">
                      Permiten recordar tus preferencias y elecciones para ofrecerte una experiencia más personalizada.
                    </p>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="marketing"
                      name="marketing"
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="marketing" className="font-medium text-gray-700">
                      Cookies de Marketing
                    </label>
                    <p className="text-gray-500">
                      Se utilizan para rastrear a los visitantes en los sitios web con el fin de mostrar anuncios relevantes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={rejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Rechazar todas
                  </button>
                  <button
                    onClick={() => savePreferences(preferences)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Guardar preferencias
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
